import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { buyerAddSchema } from "../../../ValidationSchema";
import ReactSelect from "../../../Components/PortalSelect";

// react-select themed to the design tokens (light + dark)
const colourStyles = {
  control: (styles, state) => ({
    ...styles,
    minHeight: 42,
    backgroundColor: "var(--color-surface)",
    borderColor: state.isFocused ? "var(--color-primary)" : "var(--color-border)",
    borderRadius: 10,
    boxShadow: state.isFocused ? "0 0 0 3px var(--color-primary-weak)" : "none",
    "&:hover": { borderColor: "var(--color-primary)" },
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    zIndex: 30,
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: "var(--color-primary-weak)",
    borderRadius: 8,
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: "var(--color-primary)",
    fontWeight: 700,
    fontSize: 12,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: "var(--color-primary)",
    borderRadius: 8,
    ":hover": { backgroundColor: "var(--color-hover)", color: "var(--color-primary)" },
  }),
  singleValue: (styles) => ({ ...styles, color: "var(--color-text-primary)" }),
  input: (styles) => ({ ...styles, color: "var(--color-text-primary)" }),
  placeholder: (styles) => ({ ...styles, color: "var(--color-text-tertiary)" }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isFocused ? "var(--color-hover)" : "var(--color-surface)",
    color: "var(--color-text-primary)",
    ":active": { backgroundColor: "var(--color-hover)" },
  }),
};

const BuyerFakeLeadAdd = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const usertype = location?.state?.usertype;
  const [initialValues] = useState({
    username: "",
    name: "",
    email: "",
    phone: "",
    company_name: "",
    website: "",
    gst_number: "",
    designation: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    // optional order fields
    product_id: null,
    title: "",
    size: "",
    price: "",
    final_price: "",
    final_description: "",
    quantity: "",
    material_color: [],
    print_coverage: [],
    burst_factor: [],
    paper_gsm: [],
    no_of_ply: [],
    lamination: "",
    // adhesive: "",
    // sku: "",
    remark: "",
    additional_fields: [], // JSON array [{label, value}]
    category: "",
  });

  // Same static option lists Product/store.jsx uses for these fields.
  const PAPER_GSM_OPTIONS = [
    { label: "100gsm", value: "100gsm" },
    { label: "120gsm", value: "120gsm" },
    { label: "150gsm", value: "150gsm" },
    { label: "180gsm", value: "180gsm" },
    { label: "220gsm", value: "220gsm" },
    { label: "above", value: "above" },
  ];
  const NO_OF_PLY_OPTIONS = [
    { label: "1Ply", value: "1Ply" },
    { label: "2Ply", value: "2Ply" },
    { label: "5Ply", value: "5Ply" },
  ];

  const [categoryData, setCategoryData] = useState([]);

  const [showProductList, setShowProductList] = useState(false);
  const [categoryId, setCategoryId] = useState(null);

  const [products, setProducts] = useState([]);
  const [title, setTitle] = useState("");

  const ProductData = () => {
    axios
      .get(`${process.env.REACT_APP_URL}/admin/products`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {
        if (res?.data?.data.products && Array.isArray(res.data.data.products)) {
          // Filter products by selected category
          const filteredProducts = res.data.data.products
            .filter((item) => item.category === categoryId) // 🔹 filter by category
            .map((item) => ({
              label: item.title || "Untitled",
              value: item.product_id,
            }));

          setProducts(filteredProducts);
        } else {
          console.warn("Unexpected API response:", res);
          setProducts([]);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        if (err.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  const handleNextClick = async (e) => {
    e.preventDefault();

    // Validate the form
    const errors = await BuyerAdd.validateForm();

    // Mark all fields as touched so errors appear in the UI
    BuyerAdd.setTouched(
      Object.keys(BuyerAdd.initialValues).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );

    // If no errors, move to the next section
    if (Object.keys(errors).length === 0) {
      setShowProductList(true); // ✅ Valid, show next section
    } else {
      toast.error("Please fix validation errors before proceeding."); // ❌ Invalid
    }
  };

  useEffect(() => {
    if (categoryId != null) {
      ProductData();
    }
  }, [categoryId]);

  const CategoriesData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/category`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );

      const categories =
        res?.data?.data?.categories?.map((item) => ({
          label: item?.product_category_name || "Untitled",
          value: item?.product_category_id,
        })) || [];

      setCategoryData(categories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories");
    }
  };

  const BuyerAdd = useFormik({
    initialValues,
    validationSchema: buyerAddSchema,

    onSubmit: (values) => {
      // check if product_id is set
      const payload = {
        ...values,

        size: Array.isArray(values.size)
          ? JSON.stringify(values.size)
          : values.size,

        material_color: Array.isArray(values.material_color)
          ? values.material_color.join(",")
          : values.material_color,

        print_coverage: Array.isArray(values.print_coverage)
          ? values.print_coverage.join(",")
          : values.print_coverage,

        burst_factor: Array.isArray(values.burst_factor)
          ? values.burst_factor.join(",") // ✅ FIX
          : values.burst_factor,

        paper_gsm: Array.isArray(values.paper_gsm)
          ? values.paper_gsm.join(",")
          : values.paper_gsm,

        no_of_ply: Array.isArray(values.no_of_ply)
          ? values.no_of_ply.join(",")
          : values.no_of_ply,

        additional_fields: Array.isArray(values.additional_fields)
          ? JSON.stringify(values.additional_fields)
          : values.additional_fields,
      };

      const apiUrl =
        usertype === "buyer"
          ? `${process.env.REACT_APP_URL}/admin/offline_buyer`
          : `${process.env.REACT_APP_URL}/admin/offline_supplier`;
      axios
        .post(`${apiUrl}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        })
        .then((res) => {
          if (res.status === 200) {
            toast.success(res?.data?.message || "Lead saved");
            Navigate("/admin/buyer/list", { state: res.data.message });
          }
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || "Something went wrong");
        });
    },
  });

  useEffect(() => {
    CategoriesData();
  }, []);

  const f = BuyerAdd;
  const err = (k) =>
    f.errors[k] && f.touched[k] ? <span className="bla-err">{f.errors[k]}</span> : null;
  const isBuyer = usertype === "buyer";
  const detailsLabel = isBuyer ? "Buyer Details" : "Supplier Details";

  return (
    <div className="bla">
      <Toaster position="top-center" reverseOrder={false} />

      <form onSubmit={f.handleSubmit}>
        {/* page header */}
        <div className="bla-head">
          <div>
            <h1 className="bla-title">
              Add {isBuyer ? "Buyer" : "Supplier"} Lead
            </h1>
            <p className="bla-sub">
              Capture a new {isBuyer ? "buyer" : "supplier"} enquiry and its
              packaging specification.
            </p>
          </div>
          <button type="button" className="bla-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
        </div>

        {/* stepper */}
        <div className="bla-stepper">
          <div className="bla-step">
            <span className={`bla-step__dot ${showProductList ? "is-done" : "is-active"}`}>
              {showProductList ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                "1"
              )}
            </span>
            <span className={`bla-step__label ${!showProductList ? "is-strong" : ""}`}>
              {detailsLabel}
            </span>
          </div>
          <div className={`bla-step__bar ${showProductList ? "is-done" : ""}`} />
          <div className="bla-step">
            <span className={`bla-step__dot ${showProductList ? "is-active" : ""}`}>2</span>
            <span className={`bla-step__label ${showProductList ? "is-strong" : ""}`}>
              Packaging Specification
            </span>
          </div>
        </div>

        {/* Buyer / Supplier Details */}
        <div className="bla-card">
          <div className="bla-card__head">
            <span className="bla-card__num">1</span>
            <div>
              <h3 className="bla-card__title">{detailsLabel}</h3>
              <p className="bla-card__sub">Who is making the enquiry.</p>
            </div>
          </div>
          <div className="bla-card__body bla-grid">
            <div className="bla-field">
              <label className="bla-label">Name</label>
              <input className="bla-input" type="text" placeholder="Enter Name" name="name" value={f.values.name} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("name")}
            </div>
            <div className="bla-field">
              <label className="bla-label">Company Name</label>
              <input className="bla-input" type="text" placeholder="Enter Company Name" name="company_name" value={f.values.company_name} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("company_name")}
            </div>
            <div className="bla-field">
              <label className="bla-label">Mobile Number</label>
              <input className="bla-input bla-mono" type="text" placeholder="Enter Contact Number" name="phone" value={f.values.phone} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("phone")}
            </div>
            <div className="bla-field">
              <label className="bla-label">Email</label>
              <input className="bla-input" type="text" placeholder="Enter Email" name="email" value={f.values.email} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("email")}
            </div>
            <div className="bla-field">
              <label className="bla-label">Business Type</label>
              <ReactSelect
                options={[
                  { label: "Manufacturer", value: "Manufacturer" },
                  { label: "Trader", value: "Trader" },
                  { label: "Retailer", value: "Retailer" },
                ]}
                isClearable={false}
                styles={colourStyles}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                placeholder="Select Business Type"
                name="designation"
                onChange={(e) => f.setFieldValue("designation", e?.value || "")}
                value={
                  f.values.designation
                    ? { label: f.values.designation, value: f.values.designation }
                    : null
                }
              />
              {err("designation")}
            </div>
            <div className="bla-field">
              <label className="bla-label">GST Number</label>
              <input className="bla-input bla-mono" type="text" placeholder="Enter GST Number" name="gst_number" value={f.values.gst_number} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("gst_number")}
            </div>
            <div className="bla-field">
              <label className="bla-label">Address</label>
              <input className="bla-input" type="text" placeholder="Enter Address" name="address" value={f.values.address} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("address")}
            </div>
            <div className="bla-field">
              <label className="bla-label">Country</label>
              <input className="bla-input" type="text" placeholder="Enter Country" name="country" value={f.values.country} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("country")}
            </div>
            <div className="bla-field">
              <label className="bla-label">State</label>
              <input className="bla-input" type="text" placeholder="Enter State" name="state" value={f.values.state} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("state")}
            </div>
            <div className="bla-field">
              <label className="bla-label">City</label>
              <input className="bla-input" type="text" placeholder="Enter City" name="city" value={f.values.city} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("city")}
            </div>
            <div className="bla-field">
              <label className="bla-label">Pincode</label>
              <input className="bla-input bla-mono" type="text" placeholder="Enter Pincode" name="pincode" value={f.values.pincode} onChange={f.handleChange} onBlur={f.handleBlur} />
              {err("pincode")}
            </div>
          </div>
        </div>

        {/* Packaging Specification (revealed after step 1 is valid) */}
        {showProductList && (
          <div className="bla-card">
            <div className="bla-card__head">
              <span className="bla-card__num">2</span>
              <div>
                <h3 className="bla-card__title">Packaging Specification</h3>
                <p className="bla-card__sub">Product and material requirements.</p>
              </div>
            </div>
            <div className="bla-card__body bla-grid">
              <div className="bla-field">
                <label className="bla-label">Category</label>
                <ReactSelect
                  options={categoryData}
                  isClearable={false}
                  styles={colourStyles}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  placeholder="Select Category"
                  name="category"
                  onChange={(e) => {
                    setCategoryId(e?.value);
                    f?.setFieldValue("category", e?.value);
                  }}
                  value={categoryData.find((item) => item.value === f.values.category) || null}
                />
                {f?.errors?.category && f?.touched?.category && (
                  <span className="bla-err">{f.errors.category}</span>
                )}
              </div>
              <div className="bla-field">
                <label className="bla-label">Product Name</label>
                <ReactSelect
                  options={products}
                  isClearable={false}
                  styles={colourStyles}
                  placeholder="Select Product"
                  name="product_id"
                  onChange={(e) => {
                    f.setFieldValue("product_id", e?.value || null);
                    f.setFieldValue("title", e?.label || "");
                  }}
                  value={products.find((item) => item.value === f.values.product_id) || null}
                />
                {f?.errors?.product_id && f?.touched?.product_id && (
                  <span className="bla-err">{f.errors.product_id}</span>
                )}
              </div>
              <div className="bla-field">
                <label className="bla-label">Size</label>
                <input className="bla-input" type="text" placeholder="Enter Size" name="size" value={f.values.size} onChange={f.handleChange} onBlur={f.handleBlur} />
                {err("size")}
              </div>
              <div className="bla-field">
                <label className="bla-label">Quantity</label>
                <input className="bla-input bla-mono" type="text" placeholder="Enter Quantity" name="quantity" value={f.values.quantity} onChange={f.handleChange} onBlur={f.handleBlur} />
                {err("quantity")}
              </div>
              <div className="bla-field">
                <label className="bla-label">Material Color</label>
                <ReactSelect
                  isMulti
                  options={[
                    { label: "White Duplex", value: "White Duplex" },
                    { label: "Brown Kraft", value: "Brown Kraft" },
                    { label: "Art Paper", value: "Art Paper" },
                  ]}
                  isClearable={false}
                  styles={colourStyles}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  placeholder="Select Material Color"
                  name="material_color"
                  onChange={(selectedOptions) =>
                    f.setFieldValue("material_color", selectedOptions?.map((option) => option.value) || [])
                  }
                  value={f.values.material_color?.map((val) => ({ label: val, value: val })) || []}
                />
                {f?.errors?.material_color && f?.touched?.material_color && (
                  <span className="bla-err">{f.errors.material_color}</span>
                )}
              </div>
              <div className="bla-field">
                <label className="bla-label">Print Coverage</label>
                <ReactSelect
                  isMulti
                  options={[
                    { label: "Single", value: "Single" },
                    { label: "Multi", value: "Multi" },
                    { label: "No print", value: "No print" },
                  ]}
                  isClearable={false}
                  styles={colourStyles}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  placeholder="Select Print Coverage"
                  name="print_coverage"
                  onChange={(selectedOptions) =>
                    f.setFieldValue("print_coverage", selectedOptions?.map((option) => option.value) || [])
                  }
                  value={f.values.print_coverage?.map((val) => ({ label: val, value: val })) || []}
                />
                {f?.errors?.print_coverage && f?.touched?.print_coverage && (
                  <span className="bla-err">{f.errors.print_coverage}</span>
                )}
              </div>
              <div className="bla-field">
                <label className="bla-label">Burst Factor</label>
                <ReactSelect
                  isMulti
                  options={[
                    { label: "18", value: "18" },
                    { label: "20", value: "20" },
                    { label: "22", value: "22" },
                    { label: "24", value: "24" },
                    { label: "28", value: "28" },
                    { label: "35", value: "35" },
                    { label: "40", value: "40" },
                  ]}
                  isClearable={false}
                  styles={colourStyles}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  placeholder="Select Burst Factor"
                  name="burst_factor"
                  onChange={(selectedOptions) =>
                    f.setFieldValue("burst_factor", selectedOptions?.map((option) => option.value) || [])
                  }
                  value={f.values.burst_factor?.map((val) => ({ label: val, value: val })) || []}
                />
                {f?.errors?.burst_factor && f?.touched?.burst_factor && (
                  <span className="bla-err">{f.errors.burst_factor}</span>
                )}
              </div>
              <div className="bla-field">
                <label className="bla-label">Paper GSM</label>
                <ReactSelect
                  isMulti
                  options={PAPER_GSM_OPTIONS}
                  isClearable={false}
                  styles={colourStyles}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  placeholder="Select Paper GSM"
                  name="paper_gsm"
                  onChange={(selectedOptions) =>
                    f.setFieldValue("paper_gsm", selectedOptions?.map((option) => option.value) || [])
                  }
                  value={f.values.paper_gsm?.map((val) => ({ label: val, value: val })) || []}
                />
                {f?.errors?.paper_gsm && f?.touched?.paper_gsm && (
                  <span className="bla-err">{f.errors.paper_gsm}</span>
                )}
              </div>
              <div className="bla-field">
                <label className="bla-label">No. of Ply</label>
                <ReactSelect
                  isMulti
                  options={NO_OF_PLY_OPTIONS}
                  isClearable={false}
                  styles={colourStyles}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  placeholder="Select No. of Ply"
                  name="no_of_ply"
                  onChange={(selectedOptions) =>
                    f.setFieldValue("no_of_ply", selectedOptions?.map((option) => option.value) || [])
                  }
                  value={f.values.no_of_ply?.map((val) => ({ label: val, value: val })) || []}
                />
                {f?.errors?.no_of_ply && f?.touched?.no_of_ply && (
                  <span className="bla-err">{f.errors.no_of_ply}</span>
                )}
              </div>
              <div className="bla-field">
                <label className="bla-label">Lamination</label>
                <ReactSelect
                  options={[
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ]}
                  isClearable={false}
                  styles={colourStyles}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  placeholder="Select Lamination"
                  name="lamination"
                  onChange={(e) => f.setFieldValue("lamination", e?.value || "")}
                  value={
                    f.values.lamination
                      ? { label: f.values.lamination, value: f.values.lamination }
                      : null
                  }
                />
                {f?.errors?.lamination && f?.touched?.lamination && (
                  <span className="bla-err">{f.errors.lamination}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* sticky action bar */}
        <div className="bla-actions">
          <div className="bla-actions__hint">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            {showProductList
              ? "Review the packaging details, then save the lead."
              : `Fill in the ${isBuyer ? "buyer" : "supplier"} details to continue.`}
          </div>
          <div className="bla-actions__btns">
            <button type="button" className="bla-btn" onClick={() => Navigate(-1)}>
              Cancel
            </button>
            {!showProductList ? (
              <button type="button" className="bla-btn bla-btn--primary" onClick={handleNextClick}>
                Continue
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ) : (
              <button type="submit" className="bla-btn bla-btn--primary" disabled={f.isSubmitting}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Save Lead
              </button>
            )}
          </div>
        </div>
      </form>

      <style>{`
.bla{ color:var(--color-text-primary); padding-bottom:80px; }
.bla *{ box-sizing:border-box; }
.bla-mono{ font-family:var(--font-mono); }

.bla-head{ display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:18px; }
.bla-title{ margin:0; font-size:24px; font-weight:800; letter-spacing:-.025em; }
.bla-sub{ margin:5px 0 0; font-size:13.5px; color:var(--color-text-secondary); }
.bla-btn{ display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 14px; border-radius:10px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-primary); font-family:inherit; font-size:12.5px; font-weight:700; cursor:pointer; transition:background .15s ease; }
.bla-btn:hover{ background:var(--color-hover); }
.bla-btn--primary{ border:0; color:#fff; height:40px; padding:0 18px; font-size:13px; background:linear-gradient(135deg,var(--color-primary),var(--color-primary-2)); box-shadow:0 2px 8px rgba(79,70,229,.3); }
.bla-btn--primary:hover{ filter:brightness(1.06); }
.bla-btn:disabled{ opacity:.65; cursor:not-allowed; }

.bla-stepper{ background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card); padding:16px 22px; display:flex; align-items:center; margin-bottom:16px; }
.bla-step{ display:flex; align-items:center; gap:10px; flex:0 0 auto; }
.bla-step__dot{ width:26px; height:26px; border-radius:50%; background:var(--color-surface-2); color:var(--color-text-tertiary); border:2px solid var(--color-border); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:800; }
.bla-step__dot.is-active{ background:var(--color-primary); border-color:var(--color-primary); color:#fff; }
.bla-step__dot.is-done{ background:var(--color-success-text); border-color:var(--color-success-text); color:#fff; }
.bla-step__label{ font-size:12.5px; font-weight:600; color:var(--color-text-tertiary); }
.bla-step__label.is-strong{ font-weight:700; color:var(--color-text-primary); }
.bla-step__bar{ flex:1; height:2px; background:var(--color-border); margin:0 14px; border-radius:2px; }
.bla-step__bar.is-done{ background:var(--color-success-text); }

.bla-card{ background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card); overflow:hidden; margin-bottom:16px; }
.bla-card__head{ display:flex; align-items:center; gap:12px; padding:16px 22px; border-bottom:1px solid var(--color-border-soft); }
.bla-card__num{ width:28px; height:28px; border-radius:50%; background:var(--color-primary); color:#fff; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:800; flex:0 0 28px; }
.bla-card__title{ margin:0; font-size:14.5px; font-weight:700; }
.bla-card__sub{ margin:2px 0 0; font-size:12px; color:var(--color-text-secondary); }
.bla-card__body{ padding:22px; }

.bla-grid{ display:grid; grid-template-columns:1fr 1fr; gap:20px 24px; }
@media (max-width:640px){ .bla-grid{ grid-template-columns:1fr; } }
.bla-field{ display:flex; flex-direction:column; }
.bla-label{ font-size:12px; font-weight:700; color:var(--color-text-secondary); margin-bottom:7px; }
.bla-req{ color:var(--color-danger-text); }
.bla-input{ width:100%; height:42px; padding:0 13px; border:1px solid var(--color-border); border-radius:10px; background:var(--color-surface); font-family:inherit; font-size:13.5px; font-weight:600; color:var(--color-text-primary); outline:none; transition:border-color .15s ease, box-shadow .15s ease; }
.bla-input::placeholder{ color:var(--color-text-tertiary); font-weight:500; }
.bla-input:focus{ border-color:var(--color-primary); box-shadow:0 0 0 3px var(--color-primary-weak); }
.bla-err{ font-size:12px; color:var(--color-danger-text); margin-top:6px; }

.bla-actions{ position:sticky; bottom:16px; z-index:15; margin-top:4px; background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card-hover); padding:14px 18px; display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.bla-actions__hint{ display:flex; align-items:center; gap:8px; font-size:12.5px; color:var(--color-text-secondary); }
.bla-actions__btns{ margin-left:auto; display:flex; align-items:center; gap:10px; }
@media (max-width:560px){ .bla-actions__hint{ display:none; } .bla-actions__btns{ margin-left:0; width:100%; } .bla-actions__btns .bla-btn{ flex:1; justify-content:center; } }
      `}</style>
    </div>
  );
};

export default BuyerFakeLeadAdd;
